import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  CollectionReference,
  addDoc,
  collection,
  deleteDoc,
  doc,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { FirebaseService } from '../../_firebase/firebase.service';
import { Evenement } from '../../_interfaces/evenement';
import { Observable, combineLatest, map, of, switchMap } from 'rxjs';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class EvenementService {
  private authService: AuthService = inject(AuthService);
  private firebase: FirebaseService = inject(FirebaseService);
  private firestore: Firestore = this.firebase.firestore;
  private evenementsCol: CollectionReference = collection(this.firestore, 'evenements');

  private readPublicsOnly(): Observable<Evenement[]> {
    const q = query(this.evenementsCol, where('private', '==', false));
    return this.firebase.collectionData(q, { idField: 'key' }) as Observable<Evenement[]>;
  }

  private readPrivesOnly(): Observable<Evenement[]> {
    const q = query(this.evenementsCol, where('private', '==', true));
    return this.firebase.collectionData(q, { idField: 'key' }) as Observable<Evenement[]>;
  }

  private readAll(): Observable<Evenement[]> {
    return this.firebase.collectionData(this.evenementsCol, { idField: 'key' }) as Observable<Evenement[]>;
  }

  readEvenementsPrives(): Observable<Evenement[]> {
    return this.readPrivesOnly();
  }

  readEvenementsPublics(): Observable<Evenement[]> {
    return this.readPublicsOnly();
  }

  readAllEvenements(): Observable<Evenement[]> {
    return this.authService.isAuth$.pipe(
      switchMap(isAuth => (isAuth ? this.readAll() : this.readPublicsOnly())),
      map(evenements =>
        evenements.sort((a, b) => new Date(a.dateDebut).getTime() - new Date(b.dateDebut).getTime())
      )
    );
  }

  readAllFutureEvenements(): Observable<Evenement[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.readAllEvenements().pipe(
      map(evenements => evenements.filter(e => new Date(e.dateDebut) >= today))
    );
  }

  readAllFuturePublicEvenements(): Observable<Evenement[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.readPublicsOnly().pipe(
      map(evenements =>
        evenements
          .filter(e => new Date(e.dateDebut) >= today)
          .sort((a, b) => new Date(a.dateDebut).getTime() - new Date(b.dateDebut).getTime())
      )
    );
  }

  readOneEvenement(key: string): Promise<Evenement> {
    const evRef = doc(this.firestore, 'evenements', key);
    return this.firebase.getDoc(evRef).then(snapshot => {
      if (!snapshot.exists()) {
        throw new Error(`Aucun évènement trouvé avec la clé: ${key}`);
      }
      return { key: snapshot.id, ...(snapshot.data() as Omit<Evenement, 'key'>) };
    });
  }

  readXEvenements(x: number): Observable<Evenement[]> {
    return this.authService.isAuth$.pipe(
      switchMap(isAuth =>
        isAuth
          ? combineLatest([this.readPrivesOnly(), this.readPublicsOnly()])
          : combineLatest([of<Evenement[]>([]), this.readPublicsOnly()])
      ),
      map(([prives, publics]) => this.filterAndSortEvenements([...prives, ...publics], x))
    );
  }

  private filterAndSortEvenements(evenements: Evenement[], x: number): Evenement[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return evenements
      .filter(e => new Date(e.dateDebut) >= today)
      .sort((a, b) => new Date(a.dateDebut).getTime() - new Date(b.dateDebut).getTime())
      .slice(0, x);
  }

  createEvenement(evenement: Evenement, isPrivate: boolean) {
    return addDoc(this.evenementsCol, {
      nom: evenement.nom,
      description: evenement.description,
      dateDebut: evenement.dateDebut,
      dateFin: evenement.dateFin,
      lieu: evenement.lieu,
      image: evenement.image,
      private: isPrivate
    });
  }

  updateEvenement(key: string, evenement: Evenement, _oldPrivate?: boolean): Promise<void> {
    const evRef = doc(this.firestore, 'evenements', key);
    return updateDoc(evRef, {
      nom: evenement.nom,
      description: evenement.description,
      dateDebut: evenement.dateDebut,
      dateFin: evenement.dateFin,
      lieu: evenement.lieu,
      image: evenement.image,
      private: evenement.private
    });
  }

  togglePrivate(key: string, isPrivate: boolean): Promise<void> {
    const evRef = doc(this.firestore, 'evenements', key);
    return updateDoc(evRef, { private: isPrivate });
  }

  deleteEvenement(evenement: Evenement): Promise<void> {
    if (!evenement.key) {
      return Promise.reject(new Error('Impossible de supprimer un événement sans clé'));
    }
    const evRef = doc(this.firestore, 'evenements', evenement.key);
    return deleteDoc(evRef);
  }
}
